@let summary = summary$ | async;
@let facilityMetrics = (facilityMetrics$ | async) ?? [];
@let isFacilityMetricsLoading =
  overviewDashboardModalService.loadingStatus() === LoadingState.LOADING;
@let isFacilityMetricsLoaded = overviewDashboardModalService.loadingStatus() === LoadingState.READY;

<h3 class="overview-dashboard-modal__header">{{ modalData.title | translate }}</h3>
@if (summary) {
  @let formattedSummaryTotalPower = summary.totalPower | power: 'kw';
  @let formattedSummaryFreePower = summary.freePower | power: 'kw';

  <div class="overview-dashboard-modal__cards">
    <div class="overview-dashboard-modal__card">
      <div class="overview-dashboard-modal__card-title">
        {{ 'monitoring.dashboard.overview-dashboard.card.devices.title' | translate }}
      </div>

      <div
        class="overview-dashboard-modal__card-content overview-dashboard-modal__card-content--vertical"
      >
        <div class="overview-dashboard-modal__value-row">
          <span class="overview-dashboard-modal__value">{{
            summary.activeDeviceCount | formattedCount
          }}</span>
        </div>
        <div class="overview-dashboard-modal__bottom-row">
          <span class="overview-dashboard-modal__text">{{
            'monitoring.dashboard.overview-dashboard.card.devices.in-work' | translate
          }}</span>
          <div class="overview-dashboard-modal__badge">
            <span class="overview-dashboard-modal__badge-number">{{
              summary.freeDeviceCount | formattedCount
            }}</span>
            <span class="overview-dashboard-modal__badge-text">{{
              'monitoring.dashboard.overview-dashboard.card.devices.free' | translate
            }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="overview-dashboard-modal__card">
      <div class="overview-dashboard-modal__card-title">
        {{ 'monitoring.dashboard.overview-dashboard.card.hashrate.title' | translate }}
      </div>

      <div
        class="overview-dashboard-modal__card-content overview-dashboard-modal__card-content--vertical"
      >
        <div
          class="overview-dashboard-modal__content-row overview-dashboard-modal__content-row--hashrate"
        >
          <div class="overview-dashboard-modal__value-wrapper">
            <span class="overview-dashboard-modal__value">
              {{ summary.totalHashrate | hashrate: 'onlyDigit' }}
            </span>
            <span class="overview-dashboard-modal__unit">
              {{ summary.totalHashrate | hashrate: 'onlyLabel' }}
            </span>
          </div>
          <div class="overview-dashboard-modal__value-wrapper">
            <span class="overview-dashboard-modal__value">{{
              summary.totalSolHashrate | solHashrate: 'onlyDigit'
            }}</span>
            <span class="overview-dashboard-modal__unit">{{
              summary.totalSolHashrate | solHashrate: 'onlyLabel'
            }}</span>
          </div>
        </div>
        <div class="overview-dashboard-modal__bottom-row">
          <span class="overview-dashboard-modal__text">{{
            'monitoring.dashboard.overview-dashboard.card.hashrate.total' | translate
          }}</span>
        </div>
      </div>
    </div>

    <div class="overview-dashboard-modal__card">
      <div class="overview-dashboard-modal__card-title">
        {{ 'monitoring.dashboard.overview-dashboard.card.power.title' | translate }}
      </div>

      <div
        class="overview-dashboard-modal__card-content overview-dashboard-modal__card-content--horizontal"
      >
        <div class="overview-dashboard-modal__content-column">
          <div class="overview-dashboard-modal__value-row">
            <span class="overview-dashboard-modal__value">{{
              formattedSummaryTotalPower.value
            }}</span>
            @if (formattedSummaryTotalPower.unit) {
              <span class="overview-dashboard-modal__unit">{{
                formattedSummaryTotalPower.unit | translate
              }}</span>
            }
          </div>
          <span class="overview-dashboard-modal__minor-text">{{
            'monitoring.dashboard.overview-dashboard.card.power.total' | translate
          }}</span>
        </div>
        <div class="overview-dashboard-modal__content-column">
          <div class="overview-dashboard-modal__value-row">
            <span class="overview-dashboard-modal__value">{{
              formattedSummaryFreePower.value
            }}</span>
            @if (formattedSummaryFreePower.unit) {
              <span class="overview-dashboard-modal__unit">{{
                formattedSummaryFreePower.unit | translate
              }}</span>
            }
          </div>
          <span class="overview-dashboard-modal__text">{{
            'monitoring.dashboard.overview-dashboard.card.power.free' | translate
          }}</span>
        </div>
      </div>
    </div>

    <div class="overview-dashboard-modal__card">
      <div class="overview-dashboard-modal__card-title">
        {{ 'monitoring.dashboard.overview-dashboard.card.temperature.title' | translate }}
      </div>

      <div
        class="overview-dashboard-modal__card-content overview-dashboard-modal__card-content--horizontal"
      >
        <div class="overview-dashboard-modal__content-column">
          <div class="overview-dashboard-modal__value-row">
            <span class="overview-dashboard-modal__value">{{
              summary.averageTemperature | formattedTemperature
            }}</span>
            <span class="overview-dashboard-modal__unit">&deg;C</span>
          </div>
          <span class="overview-dashboard-modal__text">{{
            'monitoring.dashboard.overview-dashboard.card.temperature.average' | translate
          }}</span>
        </div>
      </div>
    </div>
  </div>
}

<div class="overview-dashboard-modal-table">
  <lib-table
    [columns]="columns"
    [dataSource]="facilityMetrics"
    [cellTemplates]="{
      totalHashrate: hashrateTpl,
      averageTemperature: averageTTpl,
      totalPower: powerTpl,
      freePower: freePowerTpl,
    }"
    [loading]="isFacilityMetricsLoading"
    [loaded]="isFacilityMetricsLoaded"
  />

  <ng-template
    #hashrateTpl
    let-row="data"
  >
    <div
      class="overview-dashboard-modal__table-cell overview-dashboard-modal__table-cell--flex-row"
    >
      <span class="overview-dashboard-modal__table-value">{{
        row.totalHashrate | hashrate: 'onlyDigit'
      }}</span>
      <span class="overview-dashboard-modal__table-unit mon-color--grey">{{
        row.totalHashrate | hashrate: 'onlyLabel'
      }}</span>
    </div>
  </ng-template>

  <ng-template
    #averageTTpl
    let-row="data"
  >
    <div
      class="overview-dashboard-modal__table-cell overview-dashboard-modal__table-cell--flex-row"
    >
      <span class="overview-dashboard-modal__table-value">{{
        row.averageTemperature | formattedTemperature
      }}</span>
      <span class="overview-dashboard-modal__table-unit mon-color--grey">&deg;C</span>
    </div>
  </ng-template>

  <ng-template
    #powerTpl
    let-row="data"
  >
    @let formattedRowTotalPower = row.totalPower | power: 'kw';

    <div
      class="overview-dashboard-modal__table-cell overview-dashboard-modal__table-cell--flex-row"
    >
      <span class="overview-dashboard-modal__table-value">{{ formattedRowTotalPower.value }}</span>
      @if (formattedRowTotalPower.unit) {
        <span class="overview-dashboard-modal__table-unit mon-color--grey">{{
          formattedRowTotalPower.unit | translate
        }}</span>
      }
    </div>
  </ng-template>

  <ng-template
    #freePowerTpl
    let-row="data"
  >
    @let formattedRowFreePower = row.freePower | power: 'kw';

    <div
      class="overview-dashboard-modal__table-cell overview-dashboard-modal__table-cell--flex-row"
    >
      <span class="overview-dashboard-modal__table-value">{{ formattedRowFreePower.value }}</span>
      @if (formattedRowFreePower.unit) {
        <span class="overview-dashboard-modal__table-unit mon-color--grey">{{
          formattedRowFreePower.unit | translate
        }}</span>
      }
    </div>
  </ng-template>
</div>
